import "source/vendor/pushstream.js"
import {
  createContext,
  useReducer,
  useState,
  useContext,
  useRef,
  useCallback,
  useEffect,
} from "react"
import { buildSubdomain } from "@planningcenter/cc-url"
import { ServerRenderedProps } from "source/shared/ServerRenderedProps"

function createPushStream() {
  let host, useSSL

  switch (buildSubdomain("push")) {
    case "http://push.churchcenter.test":
      host = "push.pco.test"
      useSSL = false
      break
    case "https://push.staging.churchcenter.com":
      host = "church-center-push-staging.planningcenteronline.com"
      useSSL = true
      break
    case "https://push.churchcenter.com":
      host = "church-center-push.planningcenteronline.com"
      useSSL = true
      break
    default:
      host = "church-center-push.planningcenteronline.com"
      useSSL = true
  }

  return new window.PushStream({
    host,
    useSSL,
    modes: "websocket",
    messagesPublishedAfter: 5,
    messagesControlByArgument: true,
  })
}

const PushStreamContext = createContext(null)

const ADD_SUBSCRIBER = "ADD_SUBSCRIBER"
const REMOVE_SUBSCRIBER = "REMOVE_SUBSCRIBER"

function subscribersReducer(array, action) {
  switch (action.type) {
    case ADD_SUBSCRIBER:
      return [...array, action.subscriber]
    case REMOVE_SUBSCRIBER:
      return array.filter((s) => s !== action.subscriber)
    default:
      throw new Error(`Unrecognized action: ${action.type}`)
  }
}

function routingKeySubscribersReducer(map, action) {
  switch (action.type) {
    case ADD_SUBSCRIBER:
    case REMOVE_SUBSCRIBER: {
      const prevSubscribers = map[action.routingKey] || []
      return {
        ...map,
        [action.routingKey]: subscribersReducer(prevSubscribers, action),
      }
    }
    default:
      throw new Error(`Unrecognized action: ${action.type}`)
  }
}

export function PushStreamProvider({ children }) {
  const [state, dispatch] = useReducer(routingKeySubscribersReducer, {})
  const [messageQueue, setMessageQueue] = useState([])
  const organizationId = useContext(ServerRenderedProps).current_organization.id

  // Create the PushStream instance
  const pushStream = useRef(null)
  if (!pushStream.current) pushStream.current = createPushStream()

  // Log errors
  const onerror = useCallback((error) => {
    console.error("PushStream error", error)
  })
  useEffect(() => {
    pushStream.current.onerror = onerror
  }, [onerror])

  // Append messages to the queue one at a time
  const onmessage = useCallback(
    (message) => setMessageQueue((prev) => [...prev, message]),
    [setMessageQueue],
  )
  useEffect(() => {
    pushStream.current.onmessage = onmessage
  }, [onmessage])

  // Process messages off the queue one at a time
  useEffect(() => {
    if (messageQueue.length === 0) return
    const message = messageQueue[0]
    const routingKey = minimalRoutingKey(message.meta.routing_key)
    const subscribers = state[routingKey] || []
    subscribers.forEach((subscriber) => subscriber(message))
    setMessageQueue((prev) => prev.slice(1))
  }, [messageQueue.length])

  // Listen only on the channels we care about
  function routingKeyToChannelName(routingKey) {
    const [app, model, _verb] = routingKey.split(".")
    return `organization-${organizationId}.${app}.church_center.v2.events.${model}`
  }
  function nonEmptyFilter(routingKey) {
    const array = state[routingKey] || []
    return array.length > 0
  }
  const channels = Object.keys(state)
    .filter(nonEmptyFilter)
    .map(routingKeyToChannelName)
    .filter(uniqFilter)
    .sort()
  useEffect(() => {
    channels.forEach((channel) => pushStream.current.addChannel(channel))
    if (channels.length > 0) pushStream.current.connect()
    return () =>
      channels.forEach((channel) => pushStream.current.removeChannel(channel))
  }, [channels.join("/")])

  return (
    <PushStreamContext.Provider value={dispatch}>
      {children}
    </PushStreamContext.Provider>
  )
}

// Abbreviated routing keys for the things that mostly do not change
// EX: "publishing.page.created"
export function usePushStream(subscriber, routingKeys) {
  const dispatch = useContext(PushStreamContext)
  useEffect(() => {
    routingKeys.forEach((routingKey) =>
      dispatch({ type: ADD_SUBSCRIBER, routingKey, subscriber }),
    )
    return () => {
      routingKeys.forEach((routingKey) =>
        dispatch({ type: REMOVE_SUBSCRIBER, routingKey, subscriber }),
      )
    }
  }, [subscriber, ...routingKeys])
}

function minimalRoutingKey(verboseRoutingKey) {
  const [app, _graph, _version, _events, model, verb] = verboseRoutingKey.split(
    ".",
  )
  return [app, model, verb].join(".")
}

function uniqFilter(item, index, array) {
  return array.indexOf(item) === index
}
